import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Star, Pencil, UserMinus, Users, Phone, Mail } from 'lucide-react';
import { assignmentsApi } from '../api';
import { AssignmentFormDialog } from './AssignmentFormDialog';
import { PersonFlag } from './AssigneeChips';
import { formatPhoneNumber } from './PhoneInput';

const MemberCard = ({ a, onEdit, onRemove }) => (
  <Card className="bg-[#161616] border-gray-800 p-4" data-testid={`team-member-${a.id}`}>
    <div className="flex justify-between items-start gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium text-white text-sm">{a.assignee?.display_name}</span>
          {a.is_primary && (
            <span className="flex items-center gap-1 text-[10px] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-1.5 py-px">
              <Star size={9} className="fill-[#D4AF37]" /> Primary
            </span>
          )}
          <PersonFlag status={a.assignee?.status} isArchived={a.assignee?.is_archived} />
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {[a.assignee?.subtitle, a.role_label].filter(Boolean).join(' · ') || 'No role set'}
        </div>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
          {a.assignee?.phone && (
            <span className="flex items-center gap-1">
              <Phone size={11} className="text-[#D4AF37]" /> {formatPhoneNumber(a.assignee.phone)}
            </span>
          )}
          {a.assignee?.email && (
            <span className="flex items-center gap-1">
              <Mail size={11} className="text-[#D4AF37]" /> {a.assignee.email}
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        {onEdit && (
          <button onClick={onEdit} className="text-gray-500 hover:text-[#D4AF37] p-1" title="Edit assignment" data-testid={`team-edit-${a.id}`}>
            <Pencil size={14} />
          </button>
        )}
        {onRemove && (
          <button onClick={onRemove} className="text-gray-500 hover:text-red-400 p-1" title="Remove from project" data-testid={`team-remove-${a.id}`}>
            <UserMinus size={15} />
          </button>
        )}
      </div>
    </div>
  </Card>
);

export const ProjectTeamTab = ({ project }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const archived = Boolean(project.is_archived);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await assignmentsApi.list({ target_type: 'project', target_id: project.id });
      setAssignments(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  const employees = assignments.filter((a) => a.assignee_type === 'employee');
  const subs = assignments.filter((a) => a.assignee_type === 'subcontractor');
  const excludeKeys = assignments.map((a) => `${a.assignee_type}:${a.assignee_id}`);

  const handleAdd = async (data) => {
    await assignmentsApi.create({
      target_type: 'project',
      target_id: project.id,
      assignee_type: data.assignee_type,
      assignee_id: data.assignee_id,
      role_label: data.role_label,
      is_primary: data.is_primary,
      internal_notes: data.internal_notes,
    });
    toast.success('Team member added');
    load();
  };

  const handleEdit = async (data) => {
    await assignmentsApi.update(editing.id, data);
    toast.success('Assignment updated');
    load();
  };

  const confirmRemove = async () => {
    try {
      await assignmentsApi.archive(removing.id);
      toast.success('Removed from project');
      setRemoving(null);
      load();
    } catch {
      toast.error('Could not remove team member');
    }
  };

  return (
    <div data-testid="project-team">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Project Team</h3>
          <p className="text-sm text-gray-500">
            Employees and subcontractors assigned to this project.
          </p>
        </div>
        {archived ? (
          <span className="text-xs text-gray-500">
            This project is archived — the team is preserved but cannot be changed.
          </span>
        ) : (
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
            data-testid="team-add-btn"
          >
            <Plus size={16} /> Add Team Member
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500" data-testid="team-loading">
          <Loader2 size={26} className="mx-auto mb-2 animate-spin text-[#D4AF37]" />
          Loading team…
        </div>
      ) : error ? (
        <Card className="bg-[#111111] border-gray-800 p-8 text-center" data-testid="team-error">
          <p className="text-red-400 mb-3">Could not load the project team.</p>
          <Button onClick={load} variant="outline" className="border-gray-700 text-gray-300">
            Retry
          </Button>
        </Card>
      ) : assignments.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="team-empty">
          <div className="bg-[#1F1F1F] rounded-2xl p-5 mb-5 inline-block">
            <Users size={36} className="text-[#D4AF37]" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No team members assigned yet.</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Add employees and subcontractors who are working on this project.
          </p>
          {!archived && (
            <Button
              onClick={() => setAddOpen(true)}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
              data-testid="team-empty-add"
            >
              <Plus size={16} /> Add Team Member
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {employees.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wide mb-2">
                Employees
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {employees.map((a) => (
                  <MemberCard
                    key={a.id}
                    a={a}
                    onEdit={!archived ? () => setEditing(a) : undefined}
                    onRemove={!archived ? () => setRemoving(a) : undefined}
                  />
                ))}
              </div>
            </div>
          )}
          {subs.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wide mb-2">
                Subcontractors
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {subs.map((a) => (
                  <MemberCard
                    key={a.id}
                    a={a}
                    onEdit={!archived ? () => setEditing(a) : undefined}
                    onRemove={!archived ? () => setRemoving(a) : undefined}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <AssignmentFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        mode="add"
        targetNoun="project"
        excludeKeys={excludeKeys}
        onSubmit={handleAdd}
      />
      <AssignmentFormDialog
        open={Boolean(editing)}
        onOpenChange={(o) => !o && setEditing(null)}
        mode="edit"
        targetNoun="project"
        assignment={editing}
        onSubmit={handleEdit}
      />

      <AlertDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="team-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from project?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {removing?.assignee?.display_name} will be removed from this project's team. The
              assignment history is preserved and the person is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="team-remove-confirm-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
